import React, { useMemo } from 'react';
import { Activity, AlertTriangle, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import {
  SEVERITY_HEX_COLORS,
  EVENT_TYPE_HEX_COLORS,
  EVENT_TYPE_LABELS,
} from '../../constants/eventStyles';
import type { SeverityLevel, EventType } from '../../redux/api/eventsApi';

interface EventStats {
  total: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  totalEnergyImpactKwh: number;
  totalCapacityAffectedKw: number;
  unresolved: number;
}

interface EventsStatsPanelProps {
  eventStats: EventStats;
}

const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    color: 'var(--color-text-primary)',
    fontSize: '12px',
  },
  itemStyle: { color: 'var(--color-text-primary)' },
  labelStyle: { color: 'var(--color-text-primary)' },
};

const EventsStatsPanel: React.FC<EventsStatsPanelProps> = ({ eventStats }) => {
  const severityPieData = useMemo(() => {
    return Object.entries(eventStats.bySeverity)
      .filter(([, count]) => count > 0)
      .map(([sev, count]) => ({
        name: sev.charAt(0).toUpperCase() + sev.slice(1),
        value: count,
        color: SEVERITY_HEX_COLORS[sev as SeverityLevel] || '#9CA3AF',
      }));
  }, [eventStats.bySeverity]);

  const eventTypeBarData = useMemo(() => {
    return Object.entries(eventStats.byType)
      .filter(([, count]) => count > 0)
      .map(([type, count]) => ({
        name: EVENT_TYPE_LABELS[type as EventType] || type,
        count,
        color: EVENT_TYPE_HEX_COLORS[type] || '#9CA3AF',
      }))
      .sort((a, b) => b.count - a.count);
  }, [eventStats.byType]);

  if (eventStats.total === 0) {
    return (
      <div className="text-center py-4 text-xs text-[var(--color-text-muted)]">
        No event data to display
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          icon={<Activity className="w-4 h-4 text-[#17c480]" />}
          label="Total Events"
          value={eventStats.total}
        />
        <MetricCard
          icon={<AlertTriangle className="w-4 h-4 text-orange-400" />}
          label="Unresolved"
          value={eventStats.unresolved}
          highlight={eventStats.unresolved > 0}
        />
        <MetricCard
          icon={<Zap className="w-4 h-4 text-yellow-400" />}
          label="Energy Impact"
          value={eventStats.totalEnergyImpactKwh > 0 ? `${(eventStats.totalEnergyImpactKwh / 1000).toFixed(1)} MWh` : '—'}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {severityPieData.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
            <h3 className="text-xs font-semibold text-[var(--color-text-primary)] mb-2">By Severity</h3>
            <div className="flex items-center">
              <div className="w-24 h-24 min-w-[96px] min-h-[96px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={severityPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={42}
                    >
                      {severityPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip {...TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1 ml-2">
                {severityPieData.map((item) => (
                  <LegendItem key={item.name} color={item.color} label={item.name} count={item.value} />
                ))}
              </div>
            </div>
          </div>
        )}

        {eventTypeBarData.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
            <h3 className="text-xs font-semibold text-[var(--color-text-primary)] mb-2">By Type</h3>
            <div className="h-24 min-h-[96px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={eventTypeBarData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 9, fill: 'var(--color-text-secondary)' }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={35}
                  />
                  <YAxis tick={{ fontSize: 9, fill: 'var(--color-text-secondary)' }} allowDecimals={false} />
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    cursor={{ fill: 'var(--color-surface-hover)', opacity: 0.5 }}
                  />
                  <Bar dataKey="count" name="Events" activeBar={false} style={{ cursor: 'default' }}>
                    {eventTypeBarData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function MetricCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`bg-[var(--color-surface)] border rounded-lg p-2.5 flex items-center gap-2.5 ${highlight ? 'border-orange-400/40' : 'border-[var(--color-border)]'}`}>
      <div className="p-1.5 bg-[var(--color-surface-hover)] rounded-md">{icon}</div>
      <div>
        <p className="text-[10px] text-[var(--color-text-secondary)] leading-tight">{label}</p>
        <p className="text-sm font-bold text-[var(--color-text-primary)]">{value}</p>
      </div>
    </div>
  );
}

function LegendItem({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
      <span className="text-[var(--color-text-secondary)] flex-1">{label}</span>
      <span className="font-medium text-[var(--color-text-primary)]">{count}</span>
    </div>
  );
}

export default EventsStatsPanel;
